import React, { useEffect, useState } from "react";
import { makeRequestAuth } from "../../../_config/api";
import { ToastContainer } from "react-toastify";
import { showSuccessToast, showErrorToast } from "../../../utils/toastUtils";
import { countries } from "./CountryName";
import {
  Globe,
  PlusCircle,
  Trash2,
  History,
  CheckCircle,
  AlertCircle,
  Clock,
  Edit3,
} from "lucide-react";

const emptyVisa = () => ({
  country: "",
  visaType: "",
  issueDate: "",
  expiryDate: "",
  denied: false,
  denialReason: "",
});

const emptyPassport = () => ({
  passportNumber: "",
  issueDate: "",
  expiryDate: "",
  remarks: "",
});

const VISAForm = () => {
  const [visas, setVisas] = useState([emptyVisa()]);
  const [oldPassports, setOldPassports] = useState({
    hasOldPassport: false,
    passports: [emptyPassport()],
  });

  const [preview, setPreview] = useState(null);
  // const [errors, setErrors] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    fetchVisa();
  }, []);

  const fetchVisa = async () => {
    setLoading(true);
    try {
      const res = await makeRequestAuth("get", "/user/getVisa");
      if (res?.data) {
        const formattedVisas = (res.data.visas || []).map(v => ({
          ...v,
          issueDate: v.issueDate ? v.issueDate.split('T')[0] : "",
          expiryDate: v.expiryDate ? v.expiryDate.split('T')[0] : ""
        }));

        const formattedPassports = (res.data.oldPassports?.passports || []).map(p => ({
          ...p,
          issueDate: p.issueDate ? p.issueDate.split('T')[0] : "",
          expiryDate: p.expiryDate ? p.expiryDate.split('T')[0] : ""
        }));

        setVisas(formattedVisas.length ? formattedVisas : [emptyVisa()]);
        setOldPassports({
          hasOldPassport: res.data.oldPassports?.hasOldPassport || false,
          passports: formattedPassports.length ? formattedPassports : [emptyPassport()]
        });

        if (res.data.visas?.length || res.data.oldPassports?.passports?.length) {
          setPreview(res.data);
        }
      }
    } catch (err) {
      console.error("Fetch Error:", err);
    } finally {
      setLoading(false);
    }
  };

  const handleVisaChange = (index, field, value) => {
    setVisas((prev) => {
      const list = [...prev];
      list[index] = { ...list[index], [field]: value };
      if (field === "denied" && !value) list[index].denialReason = "";
      return list;
    });
  };

  const handleOldPassportChange = (index, field, value) => {
    setOldPassports((prev) => {
      const passports = [...prev.passports];
      passports[index] = { ...passports[index], [field]: value };
      return { ...prev, passports };
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    // setErrors([]);
    setLoading(true);

    try {
      const payload = {
        visas,
        oldPassports: {
          hasOldPassport: oldPassports.hasOldPassport,
          passports: oldPassports.hasOldPassport ? oldPassports.passports : []
        }
      };
      const res = await makeRequestAuth("post", "/user/createOrUpdateVisa", payload);
      showSuccessToast("Travel history secured successfully ✅");
      setPreview(res.data);
    } catch (err) {
      showErrorToast(err?.response?.data?.message || "Validation Error");
    } finally {
      setLoading(false);
    }
  };

  const STATUS_CONFIG = {
    pending: { color: "bg-amber-100 text-amber-700 border-amber-200", icon: <Clock size={14} />, label: "Pending" },
    approve: { color: "bg-emerald-100 text-emerald-700 border-emerald-200", icon: <CheckCircle size={14} />, label: "Approved" },
    reject: { color: "bg-rose-100 text-rose-700 border-rose-200", icon: <AlertCircle size={14} />, label: "Rejected" },
  };

  return (
    <div className="max-w-4xl mx-auto py-10 px-4 font-normal">
      <ToastContainer />

      {/* HEADER */}
      <div className="bg-[#132b45] rounded-t-[2.5rem] p-8 text-white relative overflow-hidden shadow-2xl">
        <div className="relative z-10 flex flex-col md:flex-row justify-between items-center gap-6">
          <div className="flex items-center gap-4">
            <div className="p-3 bg-blue-500/20 rounded-2xl">
              <Globe size={30} className="text-blue-300" />
            </div>
            <div>
              <h1 className="text-2xl font-medium">VISA INFORMATION</h1>
              <p className="text-blue-100/60 text-xs mt-1 font-normal">Visa records and previous passport details</p>
            </div>
          </div>
          {preview && (
            <div className={`flex items-center gap-2 px-4 py-1.5 rounded-full border text-[10px] font-normal uppercase tracking-widest ${STATUS_CONFIG[preview.status?.toLowerCase()]?.color || "bg-slate-100 text-slate-600"}`}>
              {STATUS_CONFIG[preview.status?.toLowerCase()]?.icon} {STATUS_CONFIG[preview.status?.toLowerCase()]?.label || "Processing"}
            </div>
          )}
        </div>
      </div>

      <div className="bg-white rounded-b-[2.5rem] shadow-xl border-x border-b border-gray-100 p-8">
        {preview ? (
          /* ================= PREVIEW MODE ================= */
          <div className="space-y-8 animate-in fade-in duration-500">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {visas.map((v, i) => (
                <div key={i} className="bg-slate-50 rounded-3xl p-6 border border-slate-100">
                  <div className="flex justify-between items-center mb-4">
                    <span className="text-[10px] font-normal text-blue-600 uppercase tracking-widest">Visa {i + 1}</span>
                    {v.denied && <span className="bg-rose-100 text-rose-600 px-2 py-0.5 rounded text-[10px] font-normal">Denied Previously</span>}
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <PreviewItem label="Country" value={v.country} />
                    <PreviewItem label="Type" value={v.visaType} />
                    <PreviewItem label="Issued" value={v.issueDate} isDate />
                    <PreviewItem label="Expiry" value={v.expiryDate} isDate />
                  </div>
                </div>
              ))}
            </div>

            {oldPassports.hasOldPassport && (
              <div className="bg-[#132b45] p-6 rounded-[2rem] text-white">
                <h4 className="flex items-center gap-2 font-normal mb-4 text-blue-300 uppercase text-xs tracking-widest">
                  <History size={16} /> Old Passport Records
                </h4>
                <div className="space-y-4">
                  {oldPassports.passports.map((p, i) => (
                    <div key={i} className="grid grid-cols-2 md:grid-cols-4 gap-4 bg-white/5 p-4 rounded-2xl border border-white/10">
                      <PreviewItem label="Passport #" value={p.passportNumber} light />
                      <PreviewItem label="Issued" value={p.issueDate} isDate light />
                      <PreviewItem label="Expiry" value={p.expiryDate} isDate light />
                      <PreviewItem label="Remarks" value={p.remarks} light />
                    </div>
                  ))}
                </div>
              </div>
            )}

            {(preview.status === "reject" || !preview.status) && (
              <div className="flex justify-center pt-6">
                <button onClick={() => setPreview(null)} className="flex items-center gap-2 bg-[#132b45] text-white px-8 py-3 rounded-2xl font-normal hover:bg-black transition-all">
                  <Edit3 size={18} /> Modify Records
                </button>
              </div>
            )}
          </div>
        ) : (
          /* ================= FORM MODE ================= */
          <form onSubmit={handleSubmit} className="space-y-12">
            {/* VISA SECTION */}
            <div className="space-y-6">
              <div className="flex justify-between items-center px-2">
                <h3 className="text-xs font-normal uppercase tracking-[0.2em] text-slate-400">Visa Information</h3>
                <button type="button" onClick={() => setVisas([...visas, emptyVisa()])} className="flex items-center gap-1 text-[#132b45] font-normal text-xs hover:opacity-70 transition-all">
                  <PlusCircle size={16} /> Add Record
                </button>
              </div>

              {visas.map((v, i) => (
                <div key={i} className="group border-2 border-slate-50 rounded-[2rem] p-8 bg-white hover:border-blue-100 transition-all relative">
                  <div className="flex justify-between items-center mb-6">
                    <span className="w-8 h-8 rounded-full bg-slate-100 text-slate-500 flex items-center justify-center font-normal text-xs">0{i + 1}</span>
                    {visas.length > 1 && (
                      <button type="button" onClick={() => setVisas(visas.filter((_, idx) => idx !== i))} className="text-rose-300 hover:text-rose-500 transition-colors">
                        <Trash2 size={20} />
                      </button>
                    )}
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="space-y-2">
                      <label className="text-[10px] font-normal uppercase text-slate-500 ml-1 tracking-widest">Country</label>
                      <select
                        value={v.country}
                        onChange={(e) => handleVisaChange(i, "country", e.target.value)}
                        className="w-full bg-slate-50 border-2 border-slate-50 rounded-2xl px-5 py-3.5 focus:border-[#132b45] focus:bg-white outline-none transition-all cursor-pointer text-sm font-normal"
                        required
                      >
                        <option value="">Select Country</option>
                        {countries.map(c => <option key={c} value={c}>{c}</option>)}
                      </select>
                    </div>

                    <InputField label="Visa Type" value={v.visaType} onChange={(val) => handleVisaChange(i, "visaType", val)} placeholder="Work, Tourist, etc." required />

                    <InputField
                      type="date"
                      label="Date of Issue"
                      value={v.issueDate}
                      max={new Date().toISOString().split('T')[0]}
                      onChange={(val) => handleVisaChange(i, "issueDate", val)}
                    />

                    <InputField
                      type="date"
                      label="Date of Expiry"
                      value={v.expiryDate}
                      min={v.issueDate}
                      onChange={(val) => handleVisaChange(i, "expiryDate", val)}
                    />

                    <div className="md:col-span-2 mt-2 p-4 bg-slate-50 rounded-2xl flex items-center justify-between">
                      <span className="text-sm font-normal text-slate-700 uppercase tracking-tight">Have you ever been denied a visa for this country?</span>
                      <button
                        type="button"
                        onClick={() => handleVisaChange(i, "denied", !v.denied)}
                        className={`w-14 h-8 rounded-full relative transition-all duration-300 ${v.denied ? 'bg-rose-500' : 'bg-slate-300'}`}
                      >
                        <div className={`absolute top-1 bg-white w-6 h-6 rounded-full transition-all duration-300 ${v.denied ? 'left-7' : 'left-1'}`} />
                      </button>
                    </div>

                    {v.denied && (
                      <div className="md:col-span-2 animate-in zoom-in-95">
                        <label className="text-[10px] font-normal uppercase text-rose-500 ml-1 tracking-widest">Reason for Denial</label>
                        <textarea
                          value={v.denialReason}
                          onChange={(e) => handleVisaChange(i, "denialReason", e.target.value)}
                          className="w-full bg-white border-2 border-rose-100 rounded-2xl px-5 py-3.5 focus:border-rose-500 outline-none transition-all text-sm mt-1 font-normal"
                          placeholder="Please explain why the visa was refused..."
                          rows="3"
                          required
                        />
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>

            {/* OLD PASSPORT SECTION */}
            <div className="space-y-6 pt-6 border-t border-slate-100">
              <h3 className="text-xs font-normal uppercase tracking-[0.2em] text-slate-400">Previous Passport Details</h3>

              <div className="flex gap-4">
                <button type="button" onClick={() => setOldPassports({ ...oldPassports, hasOldPassport: true })} className={`flex-1 py-4 rounded-2xl font-normal text-xs uppercase transition-all ${oldPassports.hasOldPassport ? 'bg-[#132b45] text-white shadow-xl' : 'bg-slate-50 text-slate-400 border-2 border-transparent hover:border-slate-200'}`}>Yes, I have old passports</button>
                <button type="button" onClick={() => setOldPassports({ ...oldPassports, hasOldPassport: false })} className={`flex-1 py-4 rounded-2xl font-normal text-xs uppercase transition-all ${!oldPassports.hasOldPassport ? 'bg-[#132b45] text-white shadow-xl' : 'bg-slate-50 text-slate-400 border-2 border-transparent hover:border-slate-200'}`}>No, I don't</button>
              </div>

              {oldPassports.hasOldPassport && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 animate-in slide-in-from-top-4">
                  {oldPassports.passports.map((p, i) => (
                    <div key={i} className="bg-slate-50/50 border-2 border-dashed border-slate-200 rounded-[2rem] p-6 space-y-4 relative group">
                      <div className="flex justify-between items-center">
                        <span className="text-[10px] font-normal text-slate-400">PASSPORT {i + 1}</span>
                        {oldPassports.passports.length > 1 && <button type="button" onClick={() => setOldPassports({ ...oldPassports, passports: oldPassports.passports.filter((_, idx) => idx !== i) })} className="text-rose-300 hover:text-rose-500"><Trash2 size={16} /></button>}
                      </div>
                      <InputField label="Passport #" value={p.passportNumber} onChange={(v) => handleOldPassportChange(i, "passportNumber", v)} required />
                      <div className="grid grid-cols-2 gap-3">
                        <InputField type="date" label="Issue Date" value={p.issueDate} max={new Date().toISOString().split('T')[0]} onChange={(v) => handleOldPassportChange(i, "issueDate", v)} />
                        <InputField type="date" label="Expiry Date" value={p.expiryDate} min={p.issueDate} onChange={(v) => handleOldPassportChange(i, "expiryDate", v)} />
                      </div>
                      <InputField label="Remarks" value={p.remarks} onChange={(v) => handleOldPassportChange(i, "remarks", v)} />
                    </div>
                  ))}
                  <button type="button" onClick={() => setOldPassports({ ...oldPassports, passports: [...oldPassports.passports, emptyPassport()] })} className="border-2 border-dashed border-slate-200 rounded-[2rem] p-8 text-slate-400 hover:text-[#132b45] hover:border-[#132b45] transition-all flex flex-col items-center gap-2">
                    <PlusCircle size={24} />
                    <span className="text-[10px] font-normal uppercase tracking-widest">Add Another</span>
                  </button>
                </div>
              )}
            </div>

            <button type="submit" disabled={loading} className="w-full bg-[#132b45] hover:bg-black text-white font-normal py-5 rounded-[2rem] shadow-2xl transition-all active:scale-[0.98] flex items-center justify-center gap-3">
              {loading ? "Saving Information..." : <><CheckCircle size={20} /> Save VISA INFORMATION</>}
            </button>
          </form>
        )}
      </div>
    </div>
  );
};

/* --- REUSABLE COMPONENTS --- */

const InputField = ({ label, value, onChange, type = "text", placeholder, required, max, min }) => (
  <div className="space-y-2">
    <label className="text-[10px] font-normal uppercase text-slate-500 ml-1 tracking-widest">{label} {required && "*"}</label>
    <input
      type={type}
      value={value || ""}
      max={max}
      min={min}
      placeholder={placeholder}
      onChange={(e) => onChange(e.target.value)}
      required={required}
      className="w-full bg-slate-50 border-2 border-slate-50 rounded-2xl px-5 py-3.5 focus:bg-white focus:border-[#132b45] outline-none transition-all text-sm font-normal placeholder:text-slate-300"
    />
  </div>
);

const PreviewItem = ({ label, value, light, isDate }) => (
  <div>
    <p className={`text-[10px] uppercase font-normal mb-1 tracking-widest ${light ? "text-white/40" : "text-slate-400"}`}>{label}</p>
    <p className={`text-sm font-normal ${light ? "text-white" : "text-[#132b45]"}`}>
      {isDate && value ? new Date(value).toLocaleDateString("en-GB", { day: '2-digit', month: 'short', year: 'numeric' }) : (value || "-")}
    </p>
  </div>
);

export default VISAForm;